/** Presentation helpers. Status is NEVER carried by colour alone - every one of these returns
 * an icon AND a word, because a colour-blind security guard at 2am is the user. */
import type { Door, Severity, SweepFinding } from "../types/api.js";

export function formatTimestamp(iso: string | null): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

export function formatAge(iso: string | null): string {
  if (!iso) return "never";
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return "never";
  const seconds = Math.max(0, Math.round(ms / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

/** An alert's `detail` blob, formatted for a table cell.
 *
 * It used to be `JSON.stringify(detail, null, 2)` unconditionally, and almost every alert this
 * service raises carries exactly one key - so `{"doorId":"side-gate"}` was rendered over three
 * lines and made every row in the alerts table three times taller than its content. Fifty rows
 * of that is a page you scroll instead of a page you scan.
 *
 * Small objects go on one line; anything genuinely structured still gets indented, because for
 * those the indentation is what makes it readable. Presentation only - the value is untouched. */
export function formatDetailJson(detail: unknown): string {
  const compact = JSON.stringify(detail);
  if (compact === undefined) return "—";
  // Wide enough for the one- and two-key objects that dominate, short enough that a nested
  // object still falls through to the indented form.
  if (compact.length <= 72) return compact;
  return JSON.stringify(detail, null, 2);
}

export interface StatusLabel {
  icon: string;
  word: string;
  tone: "ok" | "warning" | "critical" | "muted";
}

export function doorStatusLabel(door: Door): StatusLabel {
  if (door.needsAttention) return { icon: "✗", word: "needs attention", tone: "critical" };
  switch (door.currentState) {
    case "closed":
      return { icon: "✓", word: "closed", tone: "ok" };
    case "unlocked":
      return { icon: "⚠", word: "unlocked", tone: "warning" };
    case "locked":
      return { icon: "⚠", word: "held locked", tone: "warning" };
    case "override_suspected":
      return { icon: "✗", word: "override suspected", tone: "critical" };
    default:
      return { icon: "?", word: "unknown", tone: "muted" };
  }
}

export function overrideLabel(door: Door): StatusLabel & { detail: string } {
  if (!door.overrideActive) {
    return { icon: "·", word: "no", tone: "muted", detail: "no override" };
  }
  if (door.overrideAuthorised) {
    // The NAME, never the id. `overrideBy` is a UUID; showing it to a person is useless, and
    // the two fields are separate precisely so that presentation can differ from identity
    // without the id column being overwritten (docs/KNOWN-PROBLEMS.md finding 12).
    const actor = door.overrideByName ?? door.overrideBy;
    // An authorised override that STILL needs attention is one a person placed and the service
    // could not confirm. It reads differently from a confirmed hold on purpose: the watchdog
    // will not revert it, so a human has to go and look.
    if (door.needsAttention) {
      return {
        icon: "✗",
        word: "UNCONFIRMED",
        tone: "critical",
        detail: actor
          ? `${actor} asked for this, but it has never been confirmed — check the door`
          : "authorised but never confirmed — check the door",
      };
    }
    return {
      icon: "⚠",
      word: "authorised",
      tone: "warning",
      detail: actor ? `by ${actor}` : "authorised",
    };
  }
  return {
    icon: "✗",
    word: "UNEXPECTED",
    tone: "critical",
    detail: "unknown — nobody authorised this",
  };
}

export function connectionLabel(door: Door): StatusLabel {
  switch (door.connectionStatus) {
    case "online":
      return { icon: "✓", word: "online", tone: "ok" };
    case "offline":
      return { icon: "✗", word: "offline", tone: "critical" };
    default:
      return { icon: "?", word: "unknown", tone: "muted" };
  }
}

export function severityTone(severity: Severity): "ok" | "warning" | "critical" {
  return severity === "critical" ? "critical" : severity === "warning" ? "warning" : "ok";
}

export function severityIcon(severity: Severity): string {
  return severity === "critical" ? "✗" : severity === "warning" ? "⚠" : "✓";
}

/** Watchdog sweep findings, through the same StatusLabel shape as everything else.
 *
 * This map used to live in WatchdogPage.tsx and stored the already-prefixed CSS class
 * (`"tone-ok"`) while every helper here returns a bare tone that callers interpolate as
 * `tone-${x}`. Both rendered correctly, but a future rename of the class scheme has no reason
 * to touch the other file and would have silently broken it (docs/FRONTEND-AUDIT.md F-P3-2).
 * One convention, in one place. */
export function sweepFindingLabel(finding: SweepFinding["finding"]): StatusLabel {
  switch (finding) {
    case "normal":
      return { icon: "✓", word: "Normal", tone: "ok" };
    case "authorised_override":
      return { icon: "⚠", word: "authorised override", tone: "warning" };
    case "unexpected_override":
      return { icon: "✗", word: "UNEXPECTED override", tone: "critical" };
    case "unreachable":
      return { icon: "✗", word: "unreachable", tone: "critical" };
    case "restored":
      return { icon: "✓", word: "restored", tone: "ok" };
    case "restore_failed":
      return { icon: "✗", word: "restore FAILED", tone: "critical" };
    default:
      return { icon: "?", word: finding, tone: "muted" };
  }
}
